import { concatStyleSets, IProgressIndicatorStyles, ISpinnerStyles, IStyle } from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { FontSizes, FontWeights, Sizes } from '../../themes/styles';

export const loaderStylesProgressIndicatorDefault = (theme: IH2OTheme): Partial<IProgressIndicatorStyles> => ({
  root: {
    position: 'relative',
    '&.ProgressIndicator-percent .ms-ProgressIndicator-itemProgress .ms-ProgressIndicator-progressBar': {
      background: 'none',
      backgroundColor: theme.semanticColors?.loaderBarIndicator,
    },
  },
  itemProgress: {
    padding: 0,
    height: 8,
    borderRadius: Sizes.borderRadius,
  },
  progressTrack: {
    backgroundColor: theme.semanticColors?.loaderBarBackground,
    height: 8,
    borderRadius: Sizes.borderRadius,
  },
  progressBar: {
    height: 8,
    borderRadius: Sizes.borderRadius,
    background: `linear-gradient(to right, ${theme.semanticColors?.loaderBarBackground} 0%, ${theme.semanticColors?.loaderBarIndicator} 50%, ${theme.semanticColors?.loaderBarBackground} 100%)`,
  },
  itemName: {
    color: theme.semanticColors?.loaderBarLabel,
    fontSize: FontSizes.xsmall,
  },
  itemDescription: {
    color: theme.semanticColors?.loaderBarDescription,
    fontSize: FontSizes.xxsmall,
    textAlign: 'right',
    position: 'absolute',
    top: 0,
    right: 0,
    paddingTop: 5,
  },
});

export const loaderStylesProgressIndicatorButton = (theme: IH2OTheme): Partial<IProgressIndicatorStyles> => ({
  root: {
    position: 'absolute',
    width: '100%',
    left: 0,
    bottom: 0,
    zIndex: 9999,
    '&.ProgressIndicator-percent .ms-ProgressIndicator-itemProgress .ms-ProgressIndicator-progressBar': {
      backgroundColor: theme.semanticColors?.loaderBarIndicatorButton,
    },
  },
  itemProgress: {
    height: 2,
    padding: 0,
    margin: `1px 2px`,
  },
  progressTrack: {
    height: 2,
  },
  progressBar: {
    height: 2,
    background: `linear-gradient(to right, ${theme.semanticColors?.loaderBarBackground} 0%, ${theme.semanticColors?.loaderBarIndicatorButton} 50%, ${theme.semanticColors?.loaderBarBackground} 100%)`,
  },
});

export const loaderStylesProgressIndicatorPercent = (theme: IH2OTheme): Partial<IProgressIndicatorStyles> => ({
  progressBar: {
    background: theme.semanticColors?.loaderBarIndicator,
  },
});
export const loaderStylesProgressIndicatorPercentMessageBar: Partial<IProgressIndicatorStyles> = {
  progressBar: {
    height: 5,
  },
  itemProgress: {
    height: 5,
    borderRadius: Sizes.borderRadius,
  },
  progressTrack: {
    height: 5,
    borderRadius: Sizes.borderRadius,
    background: 'transparent',
  },
};

export const loaderStylesProgressIndicatorMessageBarError = (theme: IH2OTheme): Partial<IProgressIndicatorStyles> => ({
  root: {
    '&.ProgressIndicator-percent .ms-ProgressIndicator-itemProgress .ms-ProgressIndicator-progressBar': {
      backgroundColor: theme.semanticColors?.messageBarIconError,
    },
  },
  progressBar: {
    backgroundColor: theme.semanticColors?.messageBarIconError,
  },
  itemName: {
    color: theme.semanticColors?.messageBarIconError,
  },
});

export const loaderStylesProgressIndicatorMessageBarSuccess = (
  theme: IH2OTheme
): Partial<IProgressIndicatorStyles> => ({
  root: {
    '&.ProgressIndicator-percent .ms-ProgressIndicator-itemProgress .ms-ProgressIndicator-progressBar': {
      backgroundColor: theme.semanticColors?.messageBarIconSuccess,
    },
  },
  progressBar: {
    backgroundColor: theme.semanticColors?.messageBarIconSuccess,
  },
  itemName: {
    color: theme.semanticColors?.messageBarIconSuccess,
  },
});

export const loaderStylesProgressIndicatorMessageBarInfo = (theme: IH2OTheme): Partial<IProgressIndicatorStyles> => ({
  root: {
    '&.ProgressIndicator-percent .ms-ProgressIndicator-itemProgress .ms-ProgressIndicator-progressBar': {
      backgroundColor: theme.semanticColors?.messageBarIconInfo,
    },
  },
  progressBar: {
    backgroundColor: theme.semanticColors?.messageBarIconInfo,
  },
  itemName: {
    color: theme.semanticColors?.messageBarIconInfo,
  },
});

export const loaderStylesProgressIndicatorMessageBarWarning = (
  theme: IH2OTheme
): Partial<IProgressIndicatorStyles> => ({
  root: {
    '&.ProgressIndicator-percent .ms-ProgressIndicator-itemProgress .ms-ProgressIndicator-progressBar': {
      backgroundColor: theme.semanticColors?.messageBarIconWarning,
    },
  },
  progressBar: {
    backgroundColor: theme.semanticColors?.messageBarIconWarning,
  },
  itemName: {
    color: theme.semanticColors?.messageBarIconWarning,
  },
});

export const loaderStylesProgressIndicatorError = (theme: IH2OTheme): Partial<IProgressIndicatorStyles> => ({
  root: {
    '&.ProgressIndicator-percent .ms-ProgressIndicator-itemProgress .ms-ProgressIndicator-progressBar': {
      backgroundColor: theme.semanticColors?.loaderBarIndicatorError,
    },
  },
  progressBar: {
    backgroundColor: theme.semanticColors?.loaderBarIndicatorError,
  },
  itemName: {
    color: theme.semanticColors?.loaderBarIndicatorError,
  },
});

const coverRootStyle: IStyle = {
  position: 'absolute',
  width: '100%',
  height: 'calc(100vh - 96px)',
  left: 0,
  top: 0,
  zIndex: 9999,
};

export const loaderStylesSpinnerDefault = (theme: IH2OTheme): Partial<ISpinnerStyles> => ({
  root: {
    display: 'flex !important',
  },
  label: {
    color: theme.semanticColors?.loaderLabel,
  },
  circle: {
    borderColor: `${theme.semanticColors?.loaderIndicator} ${theme.semanticColors?.loader} ${theme.semanticColors?.loader}`,
  },
});

export const loaderStylesSpinnerXLarge: Partial<ISpinnerStyles> = {
  root: {
    ...coverRootStyle,
  },
  circle: {
    borderWidth: 4,
    height: 60,
    width: 60,
  },
};

const buttonSpinnerHalfWidth = '11px';
export const loaderStylesSpinnerButton: Partial<ISpinnerStyles> = {
  root: {
    ...coverRootStyle,
    height: '100%',
    paddingLeft: buttonSpinnerHalfWidth,
  },
  circle: {
    borderWidth: 3,
    height: 25,
    width: 25,
  },
};

const boxSpinnerStyles: Partial<ISpinnerStyles> = {
  root: {
    ...coverRootStyle,
    flexDirection: 'row',
    height: '100%',
    backgroundColor: 'white',
    borderRadius: Sizes.borderRadius,
  },
  circle: {
    borderWidth: 2,
    height: 16,
    width: 16,
  },
  label: {
    fontWeight: FontWeights.fw500,
    margin: 0,
    marginLeft: 4,
    marginRight: 16,
  },
};

export const loaderStylesSpinnerTag = (theme: IH2OTheme): Partial<ISpinnerStyles> =>
  concatStyleSets(boxSpinnerStyles, {
    root: {
      width: 'auto',
    },
    circle: {
      borderColor: `rgb(0 0 0 / 0.5) rgb(0 0 0 / 0.15) rgb(0 0 0 / 0.15)`,
      height: 15,
      width: 15,
    },
    label: {
      color: theme.semanticColors?.buttonText,
      fontWeight: 'inherit',
    },
  });

export const loaderStylesSpinnerChat = (): Partial<ISpinnerStyles> => concatStyleSets(boxSpinnerStyles, {});

export const loaderStylesSpinnerButtonPrimary = (theme: IH2OTheme): Partial<ISpinnerStyles> =>
  concatStyleSets(boxSpinnerStyles, {
    root: {
      backgroundColor: theme.semanticColors?.buttonPrimaryBackground,
      paddingLeft: buttonSpinnerHalfWidth,
    },
    circle: {
      borderColor: `rgb(0 0 0 / 0.65) rgb(255 255 255 / 0.5) rgb(255 255 255 / 0.5)`,
    },
    label: {
      color: theme.semanticColors?.buttonPrimaryText,
      fontSize: FontSizes.button,
    },
  });

export const loaderStylesSpinnerButtonDanger = (theme: IH2OTheme): Partial<ISpinnerStyles> =>
  concatStyleSets(boxSpinnerStyles, {
    root: {
      backgroundColor: theme.semanticColors?.buttonDanger,
      paddingLeft: buttonSpinnerHalfWidth,
    },
    circle: {
      borderColor: `rgb(255 255 255 / 0.65) rgb(255 255 255 / 0.25) rgb(255 255 255 / 0.25)`,
    },
    label: {
      color: theme.palette?.white,
      fontSize: FontSizes.button,
    },
  });

export const loaderStylesSpinnerButtonSecondary = (theme: IH2OTheme): Partial<ISpinnerStyles> =>
  concatStyleSets(boxSpinnerStyles, {
    root: {
      backgroundColor: theme.semanticColors?.buttonSecondaryBackground,
      paddingLeft: buttonSpinnerHalfWidth,
    },
    circle: {
      borderColor: `rgb(255 255 255 / 0.65) rgb(255 255 255 / 0.25) rgb(255 255 255 / 0.25)`,
    },
    label: {
      color: theme.semanticColors?.buttonSecondaryText,
      fontSize: FontSizes.button,
    },
  });

export const loaderStylesSpinnerPivot: Partial<ISpinnerStyles> = {
  root: {
    marginLeft: 5,
  },
  circle: {
    borderWidth: 3,
    height: 25,
    width: 25,
  },
};

export const loaderStylesSpinnerHeight100: Partial<ISpinnerStyles> = {
  root: {
    height: '100%',
  },
};

export interface ILoaderContainerStyles {
  root?: IStyle;
}

export interface ILoaderContainerClassnames {
  root: string;
}

export const loaderContainerStylesDefault: Partial<ILoaderContainerStyles> = {
  root: {},
};
